import { localDate, startMinute, type Opportunity } from '../opportunity';

/** Day, then start time — the order events are stored and read in. */
/** How long a hidden card takes to fade and close its gap. Matches `Leave` in EventList. */
export const HIDE_MS = 440;

/**
 * A phone swipe's confirmation ("Added to Schedule"): how long it shows once the
 * card is off the screen, then how long it takes to fade. Only after both does
 * the card's row close, or, when the card is still listed, the card come back.
 */
export const SWIPE_CONFIRM_MS = 2000;
export const SWIPE_FADE_MS = 400;

export const byWhen = (a: Opportunity, b: Opportunity) =>
  localDate(a).localeCompare(localDate(b)) || (startMinute(a) ?? -1) - (startMinute(b) ?? -1);

export const FEEDBACK_URL =
  (import.meta.env.VITE_FEEDBACK_FORM_URL as string | undefined) ??
  'https://forms.gle/EMdaVKtjdm8jxN4W9';

/**
 * The lockup: "My Climate Week" by [CommonGround].
 *
 * Both marks are two-line lockups whose artwork fills its viewBox edge to edge,
 * so equal *heights* is what makes them read as the same size. Equal widths do
 * not: the two are drawn to slightly different aspect ratios, and
 * CommonGround's spends some of its width on the tree beside the wordmark
 * rather than on the wordmark itself. So the CommonGround width is derived from
 * the other mark's height, and retuning the wordmark keeps the pair matched on
 * its own.
 */
export const WORDMARK_W = { wide: 152, narrow: 118, phone: 104 };

/** Remembers that the swipe has been demonstrated once on this browser. */
export const SWIPE_HINT_KEY = 'mcw.swipeHintSeen';
/** 138.33 / 46.34 and 254.61 / 83.07 — from the two generated viewBoxes. */
export const WORDMARK_ASPECT = 138.33 / 46.34;
export const CG_ASPECT = 254.61 / 83.07;
export const CG_W = Math.round((WORDMARK_W.wide / WORDMARK_ASPECT) * CG_ASPECT);

/** Space either side of the "by". */
export const LOCKUP_GAP = 32;

/**
 * The top strip's measurements, from 900px up. They live here rather than in
 * `Frame` because the lockup's own clamp is written here and `Frame` reads this
 * file for the lockup's proportions: one of the two has to hold them.
 */
/** The strip's side padding. */
export const STRIP_X = 26;
/** What the view toggle keeps clear of on either side: the filters, the lockup, the links, the callout. */
export const TOGGLE_GAP = 16;
/** The "by" between the two marks, which the lockup's own width does not include. */
export const LOCKUP_BY = 19;

/**
 * The lockup scales as one piece, driven by a single length: the wordmark's
 * width, from which everything else follows.
 *
 *     lockup = wordmark x (1 + CG_RATIO + 2 x GAP_RATIO) + 19px for "by"
 *
 * At full size that is 391px. From 900px up it sizes itself against whatever
 * limits it: in the left sidebar, the sidebar, in container units, since that
 * is resized by hand; in the top strip, the view toggle at the window's centre.
 */
export const CG_RATIO = CG_W / WORDMARK_W.wide;
export const GAP_RATIO = LOCKUP_GAP / WORDMARK_W.wide;
export const LOCKUP_PER_WORDMARK = 1 + CG_RATIO + 2 * GAP_RATIO;

/** Full size until the sidebar is narrower than that, then exactly its width. */
export const LOCKUP_IN_SIDEBAR = `min(${WORDMARK_W.wide}px, calc((100cqw - 19px) / ${LOCKUP_PER_WORDMARK}))`;

/**
 * In the top strip, which starts at the page's left edge whenever the lockup
 * is in it: the room left of the view toggle at the screen's centre (the
 * strip's 26px padding, half the pill's width, the 16px gap and the 19px "by"),
 * down to a floor below which the wordmark stops reading. Below 900px the floor
 * binds, and `stripLockupRight` is where it lands; the toggle is then pushed
 * clear of it rather than the lockup giving up more. A toggle pushed right by
 * the filters only leaves more room, and is deliberately not fed back in here.
 */
export const LOCKUP_IN_STRIP = `clamp(114px, calc((50vw - ${STRIP_X + TOGGLE_GAP + LOCKUP_BY}px - var(--toggle-w) / 2) / ${LOCKUP_PER_WORDMARK}), ${WORDMARK_W.wide}px)`;
